import { useModal } from "@ebay/nice-modal-react"
import { ArrowRightLeft, Loader2 } from "lucide-react"
import { observer } from "mobx-react"
import { useRef, useState } from "react"
import { embeddedBrowserStore } from "../browser/store"
import { isHarnessId } from "../modelRegistry"
import type { NewTaskAgentTarget } from "../persistence/newTaskAgentPins"
import type { TaskModel } from "../store/TaskModel"
import { ulid } from "../utils/ulid"
import { SessionActionPickerModal } from "./actions/SessionActionPickerModal"

export const TaskHandoffButton = observer(function TaskHandoffButton({
    taskModel,
    pinnedTargets,
    onStarted,
    onAccepted,
}: {
    taskModel: TaskModel
    pinnedTargets?: readonly NewTaskAgentTarget[]
    onStarted?: () => void
    onAccepted?: () => void
}) {
    const picker = useModal(SessionActionPickerModal)
    const [cancelError, setCancelError] = useState<string | null>(null)
    const attempt = useRef<{
        requestId: string
        sessionId: string
        harnessId: string
        model: string
        thinking?: import("../agentTarget").ThinkingLevel
        instructions: string
    } | null>(null)
    const source = taskModel.primarySessionThreadReference
    if (!source) return null
    const pending = taskModel.pendingHandoff
    const open = () => {
        setCancelError(null)
        void picker.show({
            actionName: "Handoff",
            description:
                "Start a fresh agent with access to this conversation and workspace. It becomes the lead here, even if the current model is unavailable. Queued messages and pending follow-ups must be run or cancelled first.",
            submitLabel: pending ? "Retry handoff" : taskModel.isWorking ? "Stop and hand off" : "Handoff",
            instructionsLabel: "Optional instructions",
            currentTarget: { harnessId: source.harnessId, modelId: source.model ?? taskModel.model, thinking: taskModel.thinking },
            initialTarget:
                pending && isHarnessId(pending.harnessId) ? { harnessId: pending.harnessId, modelId: pending.model, thinking: pending.thinking } : undefined,
            initialInstructions: attempt.current?.instructions,
            pinnedTargets,
            loadHarnesses: () => taskModel.input.listSessionActionHarnesses(),
            onSelect: async ([target], instructions = "") => {
                if (embeddedBrowserStore.hasPendingUserAction(source.sessionId))
                    throw new Error("Resolve the pending Browser request before handing off this agent.")
                const current = attempt.current
                const requestId =
                    pending?.clientRequestId ??
                    (current?.sessionId === source.sessionId &&
                    current.harnessId === target.harnessId &&
                    current.model === target.modelId &&
                    current.thinking === target.thinking &&
                    current.instructions === instructions
                        ? current.requestId
                        : `handoff:${ulid()}`)
                const sessionId = pending?.sessionId ?? source.sessionId
                attempt.current = { requestId, sessionId, harnessId: target.harnessId, model: target.modelId, thinking: target.thinking, instructions }
                onStarted?.()
                await taskModel.handoffTo({
                    sessionId,
                    harnessId: target.harnessId,
                    model: target.modelId,
                    thinking: target.thinking,
                    instructions,
                    clientRequestId: requestId,
                })
                attempt.current = null
                onAccepted?.()
            },
        })
    }
    const cancel = async () => {
        setCancelError(null)
        try {
            await taskModel.cancelHandoff()
            attempt.current = null
        } catch (error) {
            setCancelError(error instanceof Error ? error.message : String(error))
        }
    }
    return (
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
            <button
                type="button"
                className="btn flex h-9 shrink-0 items-center justify-center gap-2 bg-base-100 px-3 text-sm font-medium text-base-content hover:bg-base-300 disabled:cursor-not-allowed disabled:opacity-40"
                onClick={open}
                disabled={taskModel.handoffSubmitting}
            >
                {taskModel.handoffSubmitting ? <Loader2 size={14} className="animate-spin" /> : <ArrowRightLeft size={14} />}
                {pending ? "Retry handoff" : "Handoff"}
            </button>
            {pending && (
                <button
                    type="button"
                    className="btn flex h-9 shrink-0 items-center justify-center px-3 text-sm font-medium text-muted hover:bg-base-300 disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={taskModel.handoffSubmitting}
                    onClick={() => void cancel()}
                >
                    Cancel handoff
                </button>
            )}
            {cancelError && (
                <span role="alert" className="text-xs text-error">
                    {cancelError}
                </span>
            )}
        </div>
    )
})
